import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import Script from 'next/script';

import { IsNmdpSite } from 'lib/util/site-configuration';
import fnNoOp from 'lib/util/no-op';

type WindowWithClassy = Window & {
  eg?: {
    init: () => void;
    destroy: () => void;
  };
};

export const getGoFundMeUrl = ({ goFundMeScriptUrl }: { goFundMeScriptUrl?: string }) => {
  if (!goFundMeScriptUrl) {
    return undefined;
  }

  try {
    const url = new URL(goFundMeScriptUrl);
    if (!url.searchParams.get('manual-init')) {
      url.searchParams.set('manual-init', 'true');
    }
    return url.toString();
  } catch (e) {
    return undefined;
  }
};

const GoFundMe = (): JSX.Element | null => {
  const { sitecoreContext } = useSitecoreContext();
  const router = useRouter();
  const goFundMeScriptUrl = getGoFundMeUrl(sitecoreContext);
  const shouldLoadGoFundMe = IsNmdpSite() && !!goFundMeScriptUrl;

  const onLoadGoFundMeScript = () => {
    const windowWithClassy = window as WindowWithClassy;
    if (windowWithClassy.eg) {
      windowWithClassy.eg.init();
    }
  };
  const { isReady: routerIsReady, events: routerEvents } = router ?? {
    isReady: false,
    events: { on: fnNoOp, off: fnNoOp },
  };
  const notReadyToRumble = !shouldLoadGoFundMe || !routerIsReady;

  useEffect(() => {
    if (notReadyToRumble) {
      return fnNoOp;
    }
    const windowWithClassy = window as WindowWithClassy;
    const initializeGoFundMe = () => {
      if (windowWithClassy?.eg) {
        windowWithClassy.eg.init();
      }
    };
    const onBeforeRouterChange = () => {
      if (windowWithClassy.eg) {
        windowWithClassy.eg.destroy();
      }
    };
    const onAfterRouterChange = () => {
      setTimeout(initializeGoFundMe, 50);
    };
    routerEvents.on('routeChangeStart', onBeforeRouterChange);
    routerEvents.on('routeChangeComplete', onAfterRouterChange);

    initializeGoFundMe();

    return () => {
      routerEvents.off('routeChangeStart', onBeforeRouterChange);
      routerEvents.off('routeChangeComplete', onAfterRouterChange);
    };
  }, [notReadyToRumble, routerEvents]);

  if (!shouldLoadGoFundMe) {
    return null;
  }

  return (
    <Script
      id="nmdp-classy-sdk"
      strategy="afterInteractive"
      src={goFundMeScriptUrl}
      onLoad={onLoadGoFundMeScript}
      async
    />
  );
};

export default GoFundMe;
